import { ChangeDetectionStrategy, Component, computed, effect, inject, signal } from '@angular/core';
import { Router } from '@angular/router';
import { FaIconComponent } from '@fortawesome/angular-fontawesome';
import { FormsModule } from '@angular/forms';
import { ActiveUserService } from '../../../services/active-user.service';
import { MyInfoService } from '../../../services/my-info.service';
import { PanelHostService } from '../../../services/panel-host.service';
import { AccessibilitySettingsComponent } from '../../accessibility-settings/accessibility-settings.component';
import { ImageCropUploadComponent } from '../../image-crop-upload/image-crop-upload.component';
import { InlineAlertComponent } from '../../ui/inline-alert/inline-alert.component';
import { ConfirmationService } from '../../../services/confirmation.service';
import { ButtonComponent } from '../../ui/button/button.component';
import { ContentContainerComponent } from '../../ui/content-container/content-container.component';
import { PanelBodyComponent } from '../../ui/panel-body/panel-body.component';
import { TranslocoDirective, TranslocoService } from '@jsverse/transloco';
import { marker } from '@jsverse/transloco-keys-manager/marker';
import { firstValueFrom } from 'rxjs';
import { FieldSaveTracker } from '../../../services/autosave/field-save-tracker';
import {
  FieldSaveStatus,
  FieldSaveStatusComponent,
} from '../../ui/field-save-status/field-save-status.component';
import { AppLanguage, AppLanguages } from '../../../services/language-preference.utils';

const LANGUAGE_FIELD_KEY = 'selectedLanguage';

@Component({
  selector: 'app-profile-panel',
  imports: [
    FaIconComponent,
    FormsModule,
    TranslocoDirective,
    AccessibilitySettingsComponent,
    ImageCropUploadComponent,
    InlineAlertComponent,
    ButtonComponent,
    ContentContainerComponent,
    PanelBodyComponent,
    FieldSaveStatusComponent,
  ],
  templateUrl: './profile-panel.component.html',
  styleUrls: ['./profile-panel.component.css'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ProfilePanelComponent {
  private readonly transloco = inject(TranslocoService);
  private readonly myInfoService = inject(MyInfoService);
  private readonly confirmationService = inject(ConfirmationService);
  private readonly panelHost = inject(PanelHostService);
  private readonly router = inject(Router);
  private readonly activeUserService = inject(ActiveUserService);

  readonly user = this.myInfoService.authUser;

  readonly profilePhotoLoadFailed = signal(false);

  readonly showProfilePhoto = computed(() => {
    const photoUrl = this.user()?.profilePhotoUrl;
    return Boolean(photoUrl) && !this.profilePhotoLoadFailed();
  });

  readonly showManagingOwnAccountNotice = this.activeUserService.isActingAsOther;

  readonly managingOwnAccountMessage = computed((): string => {
    const authName = this.user()?.displayName ?? '';
    const actingAsName = this.activeUserService.actingAsDisplayName();
    return this.transloco.translate(marker('profile.managing-own-account'), {
      name: authName,
      actingAsName: actingAsName ?? this.transloco.translate(marker('accountSwitch.child-account')),
    });
  });

  readonly showPhotoUpload = signal(false);
  readonly uploadingPhoto = signal(false);
  readonly removingPhoto = signal(false);
  readonly photoUploadError = signal<string | null>(null);

  readonly language = signal<AppLanguage>(
    this.transloco.getActiveLang() === AppLanguages.Spanish
      ? AppLanguages.Spanish
      : AppLanguages.English,
  );

  private readonly languageSaveTracker = new FieldSaveTracker();

  readonly languageSaveStatus = computed((): FieldSaveStatus =>
    this.languageSaveTracker.statusOf(LANGUAGE_FIELD_KEY),
  );

  readonly languageSaveStatusLabel = computed((): string | null => {
    switch (this.languageSaveStatus()) {
      case 'saving':
        return this.transloco.translate(marker('profile.saveStatus.saving'));
      case 'saved':
        return this.transloco.translate(marker('profile.saveStatus.saved'));
      case 'error':
        return this.transloco.translate(marker('profile.saveStatus.error'));
      default:
        return null;
    }
  });

  readonly activeLanguageSaveStatus = computed(
    (): Exclude<FieldSaveStatus, 'idle'> | null => {
      const status = this.languageSaveStatus();
      return status === 'idle' ? null : status;
    },
  );

  constructor() {
    effect(() => {
      this.user()?.profilePhotoUrl;
      this.profilePhotoLoadFailed.set(false);
    });
  }

  onProfilePhotoError(): void {
    this.profilePhotoLoadFailed.set(true);
  }

  startPhotoUpload(): void {
    this.photoUploadError.set(null);
    this.showPhotoUpload.set(true);
  }

  cancelPhotoUpload(): void {
    this.showPhotoUpload.set(false);
    this.photoUploadError.set(null);
  }

  async onPhotoReady(blob: Blob): Promise<void> {
    this.uploadingPhoto.set(true);
    this.photoUploadError.set(null);

    try {
      await firstValueFrom(this.myInfoService.uploadProfilePhoto(blob));
      this.showPhotoUpload.set(false);
    } catch {
      this.photoUploadError.set(this.transloco.translate('profile.photo-upload-error'));
    } finally {
      this.uploadingPhoto.set(false);
    }
  }

  async removePhoto(): Promise<void> {
    const confirmed = await this.confirmationService.confirmDelete({
      header: this.transloco.translate('profile.remove-photo-heading'),
      message: this.transloco.translate('profile.remove-photo-confirm'),
      confirmText: this.transloco.translate('profile.remove-photo-yes'),
      cancelText: this.transloco.translate('profile.cancel'),
    });
    if (!confirmed) {
      return;
    }

    this.removingPhoto.set(true);
    this.photoUploadError.set(null);

    try {
      await firstValueFrom(this.myInfoService.removeProfilePhoto());
    } catch {
      this.photoUploadError.set(this.transloco.translate('profile.photo-remove-error'));
    } finally {
      this.removingPhoto.set(false);
    }
  }

  updateLanguage(language: AppLanguage): void {
    this.language.set(language);
    this.transloco.setActiveLang(language);
    this.languageSaveTracker.markSaving([LANGUAGE_FIELD_KEY]);

    this.myInfoService.saveLanguagePreference(language).subscribe({
      next: () => {
        this.languageSaveTracker.settle([LANGUAGE_FIELD_KEY], 'saved');
      },
      error: () => {
        this.languageSaveTracker.settle([LANGUAGE_FIELD_KEY], 'error');
      },
    });
  }

  openChangeEmail(): void {
    this.panelHost.open('change-email');
  }

  openChangePassword(): void {
    this.panelHost.open('change-password');
  }

  async openMyInfo(): Promise<void> {
    await this.panelHost.dismiss('/forms');
    await this.router.navigate(['/forms'], { fragment: 'my-info' });
  }
}
